import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { getAccessToken, logout } from "./auth";

/**
 * Sur 401 partout dans l'app : on purge la session et on redirige vers /
 * (page login). Évite de garder l'UI avec data=undefined → "0 partout".
 */
function handleUnauthorized() {
  logout();
  // Redirige seulement si on n'est pas déjà sur la page login
  if (typeof window !== "undefined" && window.location.pathname !== "/") {
    window.location.href = "/";
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers: Record<string, string> = {};
  if (data) headers["Content-Type"] = "application/json";

  const token = getAccessToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
  });

  if (res.status === 401) {
    handleUnauthorized();
    throw new Error("401: session expirée");
  }

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;

    const res = await fetch(queryKey.join("/") as string, { headers });

    if (res.status === 401) {
      if (unauthorizedBehavior === "returnNull") return null;
      // Session expirée / token invalide → purge + redirect login
      handleUnauthorized();
      throw new Error("401: session expirée");
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

// Rétablissement automatique après un redéploiement backend (constat PO 05/09/2026).
// Pendant un redéploiement, les requêtes échouent en réseau/5xx pendant ~2 min,
// mais le navigateur reste « online » → refetchOnReconnect (event navigator.onLine)
// ne se déclenche jamais. On garde donc la requête en RÉESSAI (elle reste en
// chargement au lieu de tomber en erreur figée) jusqu'au retour du backend.
// throwIfResNotOk produit `${status}: ${text}` ; une erreur réseau n'a pas de
// préfixe (status 0). Les 4xx (401/403/404/400/422…) sont définitifs → pas de
// réessai, l'erreur remonte vite (et la logique de logout 401 reste intacte).
function shouldRetryTransient(failureCount: number, error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  const match = /^(\d{3}):/.exec(message);
  const status = match ? Number(match[1]) : 0;
  if (status >= 400 && status < 500 && status !== 429) return false;
  return failureCount < 8; // ~2-3 min avec le backoff plafonné à 30 s
}
const transientRetryDelay = (attemptIndex: number): number =>
  Math.min(1000 * 2 ** attemptIndex, 30000);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
      staleTime: Infinity,
      retry: shouldRetryTransient,
      retryDelay: transientRetryDelay,
    },
    mutations: {
      retry: false,
    },
  },
});

/** Stub V1.6 — pas de CSRF côté economat (Bearer JWT à la place) */
export async function getCsrfToken(): Promise<string> { return ""; }
